import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './SidebarMenuItem.module.css';

/**
 * SidebarMenuItem Component
 * Reusable menu item component for sidebar and mobile menu
 * Follows CRYPTMET design system
 */
const SidebarMenuItem = ({
  icon,
  label,
  path,
  onClick,
  isCollapsed = false,
  color,
  isVip = false,
  children,
  className = '',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === path;

  // Handle click - navigate and call onClick if provided
  const handleClick = (e) => {
    if (onClick) {
      // Call onClick handler (e.g., to close sidebar)
      onClick(e);
      // Still navigate if path is provided
      if (path) {
        navigate(path);
      }
    }
  };

  // Render VIP label with split styling
  const renderLabel = () => {
    if (isVip && label === 'VIP Club') {
      return (
        <span className={styles.vipLabel}>
          <span className={styles.vipText}>VIP</span>
          <span className={styles.clubText}>Club</span>
        </span>
      );
    }
    return label;
  };

  const activeColor = isVip ? 'var(--color-vip)' : (color || 'var(--color-text-primary)');
  const content = (
    <div className={`${styles.menuItem} ${isActive ? styles.active : ''} ${className}`}>
      <div className={styles.leftSection}>
        {icon && (
          <div className={styles.iconWrapper} style={{ color: isActive ? activeColor : undefined }}>
            {icon}
          </div>
        )}
        {!isCollapsed && (
          <span 
            className={styles.label}
            style={{ color: isActive ? activeColor : 'var(--color-text-secondary)' }}
          >
            {renderLabel()}
          </span>
        )}
      </div>
      {children}
    </div>
  );

  // If onClick is provided, render as clickable div that navigates, otherwise as Link
  if (onClick && path) {
    return (
      <div 
        onClick={handleClick} 
        className={styles.wrapper}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        {content}
      </div>
    );
  }

  // If onClick without path, just call onClick
  if (onClick) {
    return (
      <div onClick={handleClick} className={styles.wrapper}>
        {content}
      </div>
    );
  }

  return (
    <Link to={path} className={styles.wrapper}>
      {content}
    </Link>
  );
};

export default SidebarMenuItem;
